/**
 * Module dependencies.
 */

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  FormList,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { requiredRule } from 'app/core/utils/field-rules';
import { useCrudRequest } from 'app/hooks/requests/crud/use-crud-request';
import { PiParticipant } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { appRoutes } from 'app/routes';
import { CopyId } from 'app/components/atoms/copy-id/copy-id';
import { TFunction } from 'i18next';
import React from 'react';

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns => [
  {
    title: 'ID',
    dataIndex: 'id',
    size: '110px',
    key: 'id',
    render: (item: any) => <CopyId value={item.id} />
  },
  {
    title: translate('poolInvest.rules.participant'),
    dataIndex: 'participantName',
    size: '1fr',
    key: 'participantName',
    render: (item: any) => item.participantName || '—'
  },
  {
    title: translate('poolInvest.rules.startDate'),
    dataIndex: 'startDate',
    size: '130px',
    key: 'startDate',
    render: (item: any) => (item.startDate ? String(item.startDate).slice(0, 10) : '—')
  },
  {
    title: translate('poolInvest.rules.endDate'),
    dataIndex: 'endDate',
    size: '130px',
    key: 'endDate',
    render: (item: any) => (item.endDate ? String(item.endDate).slice(0, 10) : translate('poolInvest.rules.openEnded'))
  },
  {
    title: translate('poolInvest.rules.weight'),
    dataIndex: 'weight',
    size: '100px',
    key: 'weight',
    render: (item: any) => (Number(item.weight) === 0 ? translate('poolInvest.rules.excluded') : `× ${item.weight}`)
  }
];

/**
 * Add form — pick a participant and the window that applies a new weight. Weight
 * 0 means the participant does not participate during that period.
 */

const addFields = (translate: TFunction): FormList => [
  {
    type: 'selectField',
    name: 'participantId',
    label: translate('poolInvest.rules.participant'),
    rules: requiredRule(translate),
    options: {
      hook: useCrudRequest as any,
      hookProps: (_values: any, params: any) => [
        {
          key: ['poolInvestParticipants'],
          options: { interpolations: params },
          endpoint: apiEndpoints.poolInvestProjectParticipants
        }
      ],
      normalize: (items: PiParticipant[]) =>
        (items ?? []).map(item => ({ value: item.id, label: item.name || item.email || item.id }))
    }
  },
  {
    type: 'datePickerField',
    name: 'startDate',
    label: translate('poolInvest.rules.startDate'),
    rules: requiredRule(translate)
  },
  {
    type: 'datePickerField',
    name: 'endDate',
    label: translate('poolInvest.rules.endDateHint'),
    rules: undefined
  },
  {
    type: 'inputNumberField',
    name: 'weight',
    label: translate('poolInvest.rules.weightHint'),
    rules: undefined
  }
];

/**
 * Edit form — the participant is fixed; only the window and weight change.
 */

const editFields = (translate: TFunction): FormList => [
  {
    type: 'datePickerField',
    name: 'startDate',
    label: translate('poolInvest.rules.startDate'),
    rules: requiredRule(translate)
  },
  {
    type: 'datePickerField',
    name: 'endDate',
    label: translate('poolInvest.rules.endDateHint'),
    rules: undefined
  },
  {
    type: 'inputNumberField',
    name: 'weight',
    label: translate('poolInvest.rules.weightHint'),
    rules: undefined
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: String(translate('common.labels.search'))
  }
];

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType => ({
  formFields: addFields(translate)
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType => ({
  formFields: editFields(translate)
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType => ({});

/**
 * List.
 */

const list = (translate: TFunction): CrudListType => ({
  columns: listColumns(translate),
  style: { minWidth: 640 },
  route: appRoutes.dashboard.poolInvest.projectRules,
  key: ['poolInvestRules'],
  endpoint: apiEndpoints.poolInvestProjectRules,
  filters: listFilters(translate)
});

/**
 * Config.
 */

export function createPoolInvestRulesTemplate(translate: TFunction): CrudTemplate {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
